import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Wallet, Plus, Trash, ArrowsLeftRight } from '@phosphor-icons/react';
import { toast } from 'sonner';
import LoadingBar from '../components/LoadingBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Accounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [newAccount, setNewAccount] = useState({ name: '', balance: '', type: 'checking', currency: 'BRL' });

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/accounts`, { withCredentials: true });
      setAccounts(res.data);
    } catch (error) {
      toast.error('Erro ao carregar contas');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/accounts`, {
        ...newAccount,
        balance: parseFloat(newAccount.balance || 0)
      }, { withCredentials: true });
      toast.success('Conta criada!');
      setOpen(false);
      fetchAccounts();
      setNewAccount({ name: '', balance: '', type: 'checking', currency: 'BRL' });
    } catch (error) {
      toast.error('Erro ao criar conta');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/accounts/${id}`, { withCredentials: true });
      toast.success('Conta excluída');
      fetchAccounts();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  if (loading) return <LoadingBar text="Carregando contas..." />;

  const totalBalance = accounts.reduce((sum, acc) => sum + acc.balance, 0);

  return (
    <div className="p-3 sm:p-6 md:p-8 space-y-6" data-testid="accounts-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-1 sm:mb-2">Contas</h1>
          <p className="text-sm sm:text-base text-muted-foreground">Gerencie suas contas bancárias e carteiras</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-account-button"><Plus size={20} className="mr-2" />Nova Conta</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nova Conta</DialogTitle>
              <DialogDescription>Adicione uma nova conta ou carteira</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div><Label>Nome</Label><Input value={newAccount.name} onChange={(e) => setNewAccount({...newAccount, name: e.target.value})} data-testid="account-name" placeholder="Ex: Conta Corrente Nubank" required /></div>
              <div><Label>Saldo Inicial</Label><Input type="number" step="0.01" value={newAccount.balance} onChange={(e) => setNewAccount({...newAccount, balance: e.target.value})} data-testid="account-balance" placeholder="0.00" /></div>
              <div>
                <Label>Tipo</Label>
                <Select value={newAccount.type} onValueChange={(val) => setNewAccount({...newAccount, type: val})}>
                  <SelectTrigger data-testid="account-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="checking">Conta Corrente</SelectItem>
                    <SelectItem value="savings">Poupança</SelectItem>
                    <SelectItem value="investment">Investimento</SelectItem>
                    <SelectItem value="cash">Dinheiro</SelectItem>
                    <SelectItem value="credit">Cartão de Crédito</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full" data-testid="submit-account">Criar Conta</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-gradient-to-br from-primary to-primary/80 text-white">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm mb-2">Saldo Total</p>
              <p className="text-3xl sm:text-4xl md:text-5xl font-bold tabular-nums" data-testid="total-accounts-balance">
                R$ {totalBalance.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Wallet size={32} weight="bold" />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {accounts.length === 0 ? (
          <Card className="col-span-full"><CardContent className="pt-6 text-center text-muted-foreground">Nenhuma conta cadastrada</CardContent></Card>
        ) : (
          accounts.map(acc => (
            <Card key={acc.account_id} className="hover:shadow-lg transition-all duration-300 border-2 hover:border-primary/50" data-testid={`account-card-${acc.account_id}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Wallet size={24} className="text-primary" weight="bold" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{acc.name}</CardTitle>
                      <p className="text-sm text-muted-foreground capitalize">{acc.type}</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(acc.account_id)} data-testid={`delete-account-${acc.account_id}`}>
                    <Trash size={18} className="text-destructive" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Saldo Atual</p>
                    <p className={`text-3xl font-bold tabular-nums ${acc.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      R$ {acc.balance.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div className="pt-4 border-t">
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <span>Moeda</span>
                      <span className="font-semibold">{acc.currency}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default Accounts;
